/**
 * Workflow Assistant sticky-session affinity helper.
 *
 * The Otto control plane fronting the deployed workflow assistant uses the
 * `X-Workflow-Assistant-Affinity` HTTP header to pin every request from a
 * single browser session to the same backend replica. This matters because
 * the assistant keeps in-memory per-session state (CopilotSession, AG-UI
 * stream buffers, tool-execution correlation IDs) — a request that lands on
 * a different replica fails to resolve that state.
 *
 * Contract: the **server** assigns the affinity value. On the very first
 * `/workflowAssistants/*` call (when no header is sent), the response carries
 * an `X-Workflow-Assistant-Affinity` header with the value the client must
 * send on every subsequent request for the lifetime of this page load.
 *
 * This module never generates a value itself. It only:
 *   1. {@link getWorkflowAssistantAffinityHeader} — returns the header object
 *      to merge into a request's `headers`, or `{}` if we haven't yet seen
 *      a value from the server.
 *   2. {@link captureWorkflowAssistantAffinity} — reads the header off a
 *      response and remembers it so later requests carry it.
 *
 * The value lives in a module-scoped variable, so it resets on full page
 * reload. That's acceptable — the next request will simply receive a fresh
 * affinity from the server.
 */

export const WORKFLOW_ASSISTANT_AFFINITY_HEADER = 'X-Workflow-Assistant-Affinity';

let affinity: string | null = null;

/**
 * Returns the affinity header to spread into a fetch request's `headers`,
 * or an empty object if the server has not yet handed us a value.
 *
 * Usage:
 *   headers: {
 *     'Authorization': `Bearer ${token}`,
 *     ...getWorkflowAssistantAffinityHeader(),
 *   }
 */
export function getWorkflowAssistantAffinityHeader(): Record<string, string> {
  return affinity ? { [WORKFLOW_ASSISTANT_AFFINITY_HEADER]: affinity } : {};
}

/**
 * Captures the affinity value from a workflow-assistant API response so
 * subsequent requests pin to the same backend replica. Safe to call on any
 * response — a no-op when the header is absent, or when the response object
 * lacks a `headers` accessor (defensive against test mocks and unusual
 * fetch shims that omit `headers`).
 */
export function captureWorkflowAssistantAffinity(response: Response): void {
  const v = response?.headers?.get?.(WORKFLOW_ASSISTANT_AFFINITY_HEADER);
  if (v) affinity = v;
}

/**
 * Test-only helper to reset the cached affinity between tests.
 */
export function __resetWorkflowAssistantAffinityForTests(): void {
  affinity = null;
}

